import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { Clearfix } from './h5b-helpers';

export const grid = {
  padding: 30,
  col: {
    col1: (1 / 12) * 100 + '%',
    col2: (2 / 12) * 100 + '%',
    col3: (3 / 12) * 100 + '%',
    col4: (4 / 12) * 100 + '%',
    col5: (5 / 12) * 100 + '%',
    col6: (6 / 12) * 100 + '%',
    col7: (7 / 12) * 100 + '%',
    col8: (8 / 12) * 100 + '%',
    col9: (9 / 12) * 100 + '%',
    col10: (10 / 12) * 100 + '%',
    col11: (11 / 12) * 100 + '%',
    col12: (12 / 12) * 100 + '%',
  },
};

const containerSizes = [
  {
    name: 'newsletter',
    width: 650,
  },
  {
    name: 'narrow',
    width: 960,
  },
  {
    name: 'base',
    width: 1440,
  },
  {
    name: 'swiper',
    width: 1352,
  },
  {
    name: 'wide',
    width: 1440,
  },
];

export const leftPanelWidth = 272;

export const Row = styled(Clearfix)`
  margin-right: ${rem(-grid.padding / 2)};
  margin-left: ${rem(-grid.padding / 2)};
`;

export const CatalogRow = styled(Row)`
  ${({ theme }) => theme.media('md')} {
    display: flex;
    align-items: flex-start;
    /* No flex gap. CategoriesCol drops its right padding while MainCol keeps its
       15px left padding, so the side-menu→content gap is a uniform 15px (figma
       closed_categories) on every catalog page (category / product / furniture). */
    gap: 0;
    /* Drop the inherited clearfix pseudo-elements so they don't become stray
       flex items that the gap would space out. */
    &::before,
    &::after {
      content: none;
    }
  }
`;

export const Col = styled.div`
  float: left;
  min-height: ${rem(1)};
  width: 100%;
  padding-right: ${rem(grid.padding / 2)};
  padding-left: ${rem(grid.padding / 2)};
`;

const ContainerStyles = css`
  padding-right: ${rem(grid.padding / 2)};
  padding-left: ${rem(grid.padding / 2)};
  margin: auto;
  ${containerSizes.map((item) => {
    return `&.${item.name} {max-width: ${rem(item.width)};}`;
  })}
`;

export const Container = styled.div`
  ${ContainerStyles};
`;

export const ContainerClearfix = styled(Clearfix)`
  ${ContainerStyles};
`;

export const CategoriesColStyles = css`
  display: none;
  /* Panel content is leftPanelWidth (272). Keep the 15px left padding (the page
     gutter, so the panel aligns flush under the header's 272px panel area) but
     drop the right padding — the side-menu→content gap then comes solely from
     MainCol's own 15px left padding, a uniform 15px (figma closed_categories). */
  width: ${({ theme }) => rem(leftPanelWidth + theme.grid.padding / 2)};
  padding-right: 0;
  position: relative;
  ${({ theme }) => theme.media('md')} {
    display: block;
    /* Fixed-width flex column (the parent CatalogRow is flex at md+). */
    flex: 0 0 ${({ theme }) => rem(leftPanelWidth + theme.grid.padding / 2)};
    align-self: flex-start;
    margin-bottom: ${rem(40)};
    /* Stay pinned under the fixed header while scrolling the (taller) product
       column. Sticky works here because the column is a non-floated flex item
       whose parent (CatalogRow) is as tall as the product column. The +20
       matches the content container's top padding so the menu stays flush under
       the header (--header-height is measured & set by HeaderNew, 180px back). */
    position: sticky;
    top: calc(var(--header-height, ${rem(180)}) + ${rem(20)});
    /* sticky makes this a stacking context; lift it above the product column's
       z-index:10 stickers so the hover flyout (in this subtree) stays on top. */
    z-index: 40;
    /* While a side-menu flyout is open inside this column, lift the column just
       above the fixed header (z-index 45, Header.tsx) so the flyout's page-dim
       shadow covers the header too — but stay below the cart/overlays/modals
       (51, 100+). The resting 40 keeps the normal scroll behavior (the column
       slides under the header). */
    &:has(nav:not(.is-suppressed) > div:hover > .cat-flyout) {
      z-index: 46;
    }
  }
`;

export const MainColStyles = css`
  ${({ theme }) => theme.media('md')} {
    flex: 1 1 0%;
    min-width: 0;
    /* Double the side-menu→content gap from the inherited 15px to 30px on every
       catalog page (category / product / furniture). CategoriesCol drops its
       right padding, so this left padding alone is the visible gap. */
    padding-left: ${rem(grid.padding)};
  }
`;

export const CategoriesCol = styled(Col)`
  ${CategoriesColStyles};
`;

export const MainCol = styled(Col)`
  ${MainColStyles};
`;

export const Sidebar = styled(Col)`
  font-size: ${rem(14)};
  ${({ theme }) => theme.media('md')} {
    width: ${({ theme }) => theme.grid.col.col3};
  }
`;

export const SidebarTitle = styled.p`
  font-size: ${rem(16)};
  font-weight: 700;
`;

export const ContentWithSidebar = styled(Col)`
  ${({ theme }) => theme.media('md')} {
    width: ${(props: any) =>
      props.fullWidth ? '100%' : ({ theme }) => theme.grid.col.col9};
  }
`;

export const MainContainer: any = styled.div`
  width: ${({ theme }) => theme.mainContainerSize || '1440px'};
  margin: 0 auto;
  padding: 0 15px;
  min-height: ${(props: any) => props.minHeight || '100px'};

  @media all and (max-width: ${({ theme }) =>
      theme.mainContainerSize || '1440px'}) {
    width: 100%;
    margin: 0;
  }
`;
